import { BaseProps, IAddonsProps } from '../common/props';
import * as React from 'react';
import { parseISO } from 'date-fns';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { InputAddons } from '../input-addons/InputAddons';
import { cs } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '../icon/Icon';
import { mcCalendar } from '@mc/react-ui-icons';
import { Popup } from '../popover/Popup';
import { PopupActions } from '../popover/reactjs-popup/types';
import { getJavaDateTime } from '@mc/react-format';
import { DatePicker } from './DatePicker';
import { parseDateTime } from './parse';
import { MonthPicker } from './MonthPicker';

export type DateInputProps = IAddonsProps &
  BaseProps & {
    disabled?: boolean;
    readOnly?: boolean;
    fullWidth?: boolean;
    /**
     * @deprecated Použijte fullWidth, zachovánu z důvodu předu z blueprint kde to bylo.
     */
    fill?: boolean;
    value?: string;
    onChange?: (newValue?: string) => void;
    onBlur?: (newValue?: string) => void;
    onFocus?: () => void;
    locale?: Locale;
    placeholder?: string;
    showTime?: boolean;
    monthPicker?: boolean;
    convertValue?: (newValue?: Date) => Date | undefined;
    showDayOfWeek?: boolean;
    inputProps?: BaseProps & React.HTMLProps<HTMLInputElement>;
    autoFocus?: boolean;
  };

const dateFromISO = (value: string | null | undefined): Date | undefined => {
  return value === '' || value === null || value === undefined
    ? undefined
    : parseISO(value);
};

export const BaseDateInput: React.FC<DateInputProps> = ({
  value,
  className,
  locale = cs,
  showTime,
  monthPicker,
  convertValue,
  onChange,
  disabled,
  readOnly,
  fullWidth,
  addonAfter,
  addonAfterCompact,
  onBlur,
  onFocus,
  placeholder,
  children,
  showDayOfWeek,
  inputProps,
  dataTest,
  autoFocus,
  ...other
}) => {
  const dateValue = dateFromISO(value);

  const useDateFormat = showTime ? 'P p' : 'P';

  const formatDisplayValue = (date?: Date) => {
    return date ? format(date, useDateFormat, { locale }) : '';
  };

  const displayValue = formatDisplayValue(dateValue);

  const [inputValue, setInputValue] = useState<string>(displayValue);
  const popupRef = useRef<PopupActions>();
  const inputRef = useRef<HTMLInputElement>();
  const [opened, setOpened] = useState(false);

  const doPopupOpen = () => {
    popupRef.current?.open();
    setOpened(true);
    // inputRef.current?.focus();
  };

  const parseValue = (dateStr: string): Date | undefined => {
    const trimmedDateStr = dateStr?.trim() || '';
    if (trimmedDateStr === '') {
      return undefined;
    }

    // Standardní parsování dle locale
    // try {
    //   let date = parse(trimmedDateStr, useDateFormat, new Date(), { locale, withTime: !!showTime });
    //   if (!isNaN(date.getTime())) {
    //     return date;
    //   }
    // } catch (err) {
    //   return undefined;
    // }

    let result = parseDateTime(trimmedDateStr, new Date(), {
      locale,
      withTime: !!showTime,
    });

    if (isNaN(result as any)) {
      return undefined;
    }

    return result;
  };

  const editedDate = useMemo(() => {
    return parseValue(inputValue);
  }, [inputValue]);

  useEffect(() => {
    setInputValue(displayValue);
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handlePopupClose = () => {
    setOpened(false);
  };

  const callOnChangeOnBlur = (date: Date | undefined, callBlur: boolean) => {
    if (convertValue) {
      date = convertValue(date);
    }

    let newValue = getJavaDateTime(date) || undefined;
    let newDisplayValue = formatDisplayValue(date);
    if (newDisplayValue !== inputValue) {
      setInputValue(newDisplayValue);
    }
    onChange?.(newValue);
    callBlur && onBlur?.(newValue);
  };

  const doPopupClose = () => {
    popupRef.current?.close();
    inputRef.current?.focus();
  };

  const handlePickerChange = (date: Date) => {
    callOnChangeOnBlur(date, false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      // case 'ArrowDown':
      //   if (e.altKey && !opened) {
      //     doPopupOpen();
      //   }
      //   break;
      // case 'ArrowUp':
      //   if (e.altKey && opened) {
      //     doPopupClose();
      //   }
      //   break;
      case 'Enter':
        let newValue;
        if (inputValue === '') {
          newValue = undefined;
        } else {
          newValue = editedDate;
        }
        callOnChangeOnBlur(newValue, false);
        if (opened) {
          doPopupClose();
        }
        break;
    }
  };

  const inputPlaceholder = placeholder; //|| format(new Date(), useDateFormat, { locale });
  const leftPlaceholder = undefined; //format(new Date(), 'EEEEEE', { locale });

  const inputStyle = {
    ...inputProps?.style,
  };

  const weekNameSize = 16;
  const weekNameLeft = 4;
  const weekNamePadding = 4;
  if (showDayOfWeek) {
    inputStyle.paddingLeft = weekNameSize + weekNamePadding;
  }

  const input = (
    <div
      key={'input'}
      className={classNames(
        className,
        Classes.FIELD_INPUT,
        Classes.FIELD_HAS_INPUT_ACTION,
        {
          [Classes.FIELD_HAS_LEFT]: showDayOfWeek,
          [Classes.FIELD_INPUT_DISABLED]: disabled,
          [Classes.FIELD_INPUT_READONLY]: readOnly,
          [Classes.LIMIT_DATE_TIME_SIZE]: !fullWidth,
        }
      )}
      {...other}
    >
      <Popup
        nested={true}
        // @ts-ignore
        innerRef={popupRef}
        onClose={handlePopupClose}
        on={[]}
        className={Classes.DATE_INPUT_POPUP}
        trigger={
          <div key="input-wrapper" className={Classes.FIELD_INPUT_GROUP}>
            {showDayOfWeek && (
              <div
                key={'dayofweek'}
                className={Classes.FIELD_INPUT_LEFT}
                style={{ left: weekNameLeft, width: weekNameSize }}
              >
                {dateValue ? (
                  format(dateValue, 'EEEEEE', { locale })
                ) : (
                  <span className={Classes.FIELD_INPUT_LEFT_PLACEHOLDER}>
                    {leftPlaceholder}
                  </span>
                )}
              </div>
            )}
            <input
              key={'input'}
              data-test={dataTest ? `input-${dataTest}` : undefined}
              {...inputProps}
              disabled={disabled}
              placeholder={inputPlaceholder}
              readOnly={readOnly}
              onKeyDown={handleKeyDown}
              // @ts-ignore
              ref={inputRef}
              className={classNames(
                Classes.INPUT,
                Classes.DATE_INPUT_INPUT,
                inputProps?.className,
                {
                  [Classes.ACTIVE]: opened,
                }
              )}
              style={inputStyle}
              type={'text'}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={onFocus}
              onBlur={() => {
                let newValue;
                if (inputValue === '') {
                  newValue = undefined;
                } else {
                  newValue = editedDate;
                }
                callOnChangeOnBlur(newValue, true);
              }}
              autoFocus={autoFocus}
            />
            <div
              key={'icon'}
              tabIndex={-1}
              className={Classes.FIELD_INPUT_ACTION}
              onClick={disabled || readOnly ? undefined : doPopupOpen}
            >
              <Icon icon={mcCalendar} />
            </div>
          </div>
        }
      >
        {!monthPicker && (
          <DatePicker
            showTime={showTime}
            value={editedDate}
            onChange={handlePickerChange}
            onClose={() => doPopupClose()}
          />
        )}
        {monthPicker && (
          <MonthPicker
            endOfMonth={false}
            value={editedDate}
            onChange={handlePickerChange}
            onClose={() => doPopupClose()}
          />
        )}
      </Popup>
    </div>
  );

  return (
    <InputAddons
      key={'addons'}
      addonAfter={addonAfter}
      addonAfterCompact={addonAfterCompact}
      limitedSize
    >
      {input}
    </InputAddons>
  );
};
